import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import MainLayouts from "../../layout/MainLayouts";
import client from "../../api/client";
import { AlertCircle, Calendar, Users, BookOpen, Clock, ArrowRight, Loader } from "lucide-react";

// ── helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

// ── skeleton ─────────────────────────────────────────────────────────────────

function Skeleton({ width = "100%", height = 16, radius = 8, style = {} }) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
        backgroundSize: "200% 100%",
        animation: "shimmer 1.4s infinite",
        ...style,
      }}
    />
  );
}

// ── main component ────────────────────────────────────────────────────────────

export default function ScholarshipDetail() {
  const { slug } = useParams();
  const [scholarship, setScholarship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    client.get(`/scholarships/${slug}`)
      .then((res) => setScholarship(res.data?.data || res.data))
      .catch((err) => {
        const status = err.response?.status;
        if (status === 404) {
          setError("This scholarship was not found.");
        } else if (status >= 500) {
          setError("Server error loading scholarship. Please try again shortly.");
        } else if (!err.response) {
          setError("Could not reach the server. Please check your connection.");
        } else {
          setError("Failed to load scholarship. It may have been removed.");
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    client
      .post("/scholarships/initiate", { scholarship: slug, name, email, phone })
      .then((res) => {
        const paymentUrl = res.data?.payment_url;
        if (paymentUrl) {
          window.location.href = paymentUrl;
        } else {
          setError("Could not initiate payment. Please try again.");
        }
      })
      .catch((err) => {
        setError(err.response?.data?.message || "Something went wrong. Please try again.");
        setSubmitting(false);
      });
  };

  // ── loading state ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <MainLayouts>
        <style>{`@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
        <div style={{ background: "#0f172a", padding: "80px 0 60px" }}>
          <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px" }}>
            <Skeleton width={120} height={28} radius={20} style={{ marginBottom: 24 }} />
            <Skeleton width="70%" height={52} style={{ marginBottom: 16 }} />
            <Skeleton width="40%" height={20} />
          </div>
        </div>
        <div style={{ maxWidth: 1100, margin: "60px auto", padding: "0 24px", display: "grid", gridTemplateColumns: "1fr 420px", gap: 48 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[100, 90, 95, 80, 85, 70].map((w, i) => <Skeleton key={i} width={`${w}%`} height={16} />)}
          </div>
          <Skeleton height={420} radius={20} />
        </div>
      </MainLayouts>
    );
  }

  // ── error state ─────────────────────────────────────────────────────────────

  if (!scholarship) {
    return (
      <MainLayouts>
        <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, padding: "60px 24px" }}>
          <AlertCircle size={48} color="#d15424" />
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", textAlign: "center" }}>
            {error || "Scholarship not found."}
          </h2>
          <p style={{ color: "#64748b", textAlign: "center" }}>
            This scholarship may have closed or been removed.
          </p>
        </div>
      </MainLayouts>
    );
  }

  // ── main render ─────────────────────────────────────────────────────────────

  return (
    <MainLayouts>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        .sch-prose h1,.sch-prose h2,.sch-prose h3 { font-weight:700; margin:1.2em 0 0.5em; color:#0f172a; }
        .sch-prose p { margin:0 0 1em; color:#475569; line-height:1.75; }
        .sch-prose ul,.sch-prose ol { padding-left:1.4em; margin:0 0 1em; color:#475569; line-height:1.75; }
        .sch-prose li { margin-bottom:0.4em; }
        .sch-prose strong { color:#0f172a; }
        .sch-prose a { color:#d15424; }
        @media(max-width:768px) {
          .sch-grid { grid-template-columns: 1fr !important; }
          .sch-hero-inner { padding: 60px 20px 40px !important; }
          .sch-sticky { position:static !important; }
          .sch-main { padding: 32px 16px !important; }
        }
      `}</style>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)", paddingTop: 0 }}>
        <div className="sch-hero-inner" style={{ maxWidth: 900, margin: "0 auto", padding: "90px 40px 60px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(209,84,36,0.15)", border: "1px solid rgba(209,84,36,0.3)", borderRadius: 20, padding: "5px 14px", marginBottom: 24 }}>
            <span style={{ color: "#fb923c", fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>Financial Aid Application</span>
          </div>

          <h1 style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)", fontWeight: 900, color: "#fff", lineHeight: 1.15, marginBottom: 24, fontFamily: "Montserrat, sans-serif" }}>
            {scholarship.title}
          </h1>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {scholarship.deadline && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 14px" }}>
                <Calendar size={15} color="#94a3b8" />
                <span style={{ color: "#cbd5e1", fontSize: 13 }}>Deadline: {formatDate(scholarship.deadline)}</span>
              </div>
            )}
            {scholarship.onboarding_date && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 14px" }}>
                <Clock size={15} color="#94a3b8" />
                <span style={{ color: "#cbd5e1", fontSize: 13 }}>Starts: {scholarship.onboarding_date}</span>
              </div>
            )}
            {scholarship.max_recipients && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 14px" }}>
                <Users size={15} color="#94a3b8" />
                <span style={{ color: "#cbd5e1", fontSize: 13 }}>{scholarship.max_recipients} spots available</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <div className="sch-main" style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 24px 80px" }}>
        <div className="sch-grid" style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 52, alignItems: "start" }}>

          {/* Left — Details */}
          <div>
            {/* Description */}
            <div
              className="sch-prose"
              style={{ marginBottom: 40 }}
              dangerouslySetInnerHTML={{ __html: scholarship.description }}
            />

            {/* What to Expect */}
            {scholarship.what_to_expect && (
              <div style={{ marginBottom: 40 }}>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 16, fontFamily: "Montserrat, sans-serif" }}>
                  What Happens After You Apply
                </h2>
                <div
                  className="sch-prose"
                  dangerouslySetInnerHTML={{ __html: scholarship.what_to_expect }}
                />
              </div>
            )}

            {/* Attached Courses */}
            {scholarship.courses?.length > 0 && (
              <div style={{ marginBottom: 40 }}>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 16, fontFamily: "Montserrat, sans-serif" }}>
                  Programme Covered
                </h2>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {scholarship.courses.map((c) => (
                    <div
                      key={c.id}
                      style={{ display: "flex", alignItems: "center", gap: 8, background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 16px" }}
                    >
                      <BookOpen size={15} color="#294a93" />
                      <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>{c.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary card */}
            <div style={{ background: "#f8fafc", borderRadius: 16, border: "1px solid #e2e8f0", padding: 28 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 20 }}>
                Scholarship Details
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {[
                  scholarship.deadline && { label: "Application Deadline", value: formatDate(scholarship.deadline) },
                  scholarship.onboarding_date && { label: "Onboarding Date", value: scholarship.onboarding_date },
                  scholarship.max_recipients && { label: "Available Spots", value: `${scholarship.max_recipients} recipients` },
                ]
                  .filter(Boolean)
                  .map(({ label, value }) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: 16 }}>
                      <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>{label}</span>
                      <span style={{ fontSize: 14, color: "#0f172a", fontWeight: 700 }}>{value}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Right — Sticky Form */}
          <div className="sch-sticky" style={{ position: "sticky", top: 100 }}>
            <div style={{ background: "#fff", borderRadius: 24, border: "1px solid #e2e8f0", boxShadow: "0 20px 60px rgba(0,0,0,0.08)", overflow: "hidden" }}>

              {/* Form header */}
              <div style={{ background: "linear-gradient(135deg, #0f172a, #1e3a5f)", padding: "28px 32px" }}>
                <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>
                  Apply for this scholarship
                </p>
                <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>{scholarship.title}</div>
              </div>

              {/* Form body */}
              <div style={{ padding: "28px 32px" }}>
                {error && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "12px 14px", marginBottom: 20 }}>
                    <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                    <p style={{ fontSize: 13, color: "#dc2626", margin: 0 }}>{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {[
                    { label: "Full Name", type: "text", value: name, setter: setName, placeholder: "John Doe", required: true },
                    { label: "Email Address", type: "email", value: email, setter: setEmail, placeholder: "john@example.com", required: true },
                    { label: "Phone Number", type: "tel", value: phone, setter: setPhone, placeholder: "+234 800 000 0000", required: true },
                  ].map(({ label, type, value, setter, placeholder, required }) => (
                    <div key={label}>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {label}
                      </label>
                      <input
                        type={type}
                        required={required}
                        value={value}
                        onChange={(e) => setter(e.target.value)}
                        placeholder={placeholder}
                        disabled={submitting}
                        style={{
                          width: "100%",
                          background: "#f9fafb",
                          border: "1.5px solid #e5e7eb",
                          borderRadius: 10,
                          padding: "11px 14px",
                          fontSize: 14,
                          color: "#111827",
                          outline: "none",
                          boxSizing: "border-box",
                          transition: "border-color 0.15s",
                          opacity: submitting ? 0.6 : 1,
                        }}
                        onFocus={(e) => (e.target.style.borderColor = "#d15424")}
                        onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                      />
                    </div>
                  ))}

                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      width: "100%",
                      background: submitting ? "#9ca3af" : "#d15424",
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 15,
                      borderRadius: 12,
                      padding: "14px 0",
                      border: "none",
                      cursor: submitting ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      marginTop: 4,
                      transition: "background 0.2s",
                    }}
                  >
                    {submitting ? (
                      <>
                        <Loader size={16} style={{ animation: "spin 0.8s linear infinite" }} />
                        Redirecting to payment…
                      </>
                    ) : (
                      <>
                        Proceed to Payment
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </form>

                <p style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
                  You'll be redirected to a secure payment page. After payment, you'll receive a confirmation email with next steps.
                </p>
              </div>
            </div>

            {/* Help */}
            <p style={{ textAlign: "center", fontSize: 12, color: "#94a3b8", marginTop: 16 }}>
              Questions?{" "}
              <a href="mailto:hello@e1xacademy.net" style={{ color: "#d15424", textDecoration: "none", fontWeight: 600 }}>
                hello@e1xacademy.net
              </a>
            </p>
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </MainLayouts>
  );
}
